import React, { useState } from 'react';
import {
  Users,
  TrendingUp,
  Activity,
  Calendar,
  RefreshCw,
  Loader2,
  Building,
  Tag,
  IndianRupee,
  Plus,
  Minus,
  ZoomIn,
  Move,
  RotateCcw,
  Menu,
} from 'lucide-react';

const formatRevenue = (value: number) => {
  if (value >= 10000000) {
    const val = value / 10000000;
    return `₹${val % 1 === 0 ? val : val.toFixed(2)} Cr`;
  }
  if (value >= 100000) {
    const val = value / 100000;
    return `₹${val % 1 === 0 ? val : val.toFixed(2)} L`;
  }
  return `₹${value.toLocaleString()}`;
};

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/hooks/redux';
import { useDashboard } from '@/hooks/useDashboard';
import { OverviewItem } from '@/api/dashboard';
import { cn } from '@/utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  tooltip?: string;
}

function MetricCard({ title, value, icon: Icon, colorClass, tooltip }: MetricCardProps) {
  return (
    <Card className="border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-bold text-muted-foreground">{title}</CardTitle>
        <div className={`p-2 rounded-md ${colorClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div
          className="text-3xl font-extrabold tracking-tight text-foreground cursor-help select-none"
          title={tooltip}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

import Chart from 'react-apexcharts';

interface RevenueChartProps {
  hourlyData?: OverviewItem[];
  weeklyData?: OverviewItem[];
  monthlyData?: OverviewItem[];
}

function RevenueChart({ hourlyData = [], weeklyData = [], monthlyData = [] }: RevenueChartProps) {
  // Determine primary overview list (hourly -> weekly -> monthly)
  const rawList =
    hourlyData.length > 0
      ? hourlyData
      : weeklyData.length > 0
        ? weeklyData
        : monthlyData;

  if (!rawList || rawList.length === 0) {
    return (
      <div className="h-[350px] flex items-center justify-center text-muted-foreground text-sm border border-dashed rounded-lg">
        No revenue data available.
      </div>
    );
  }

  const categories = rawList.map((item) => {
    if (item.hour) {
      const parts = item.hour.split(' ');
      const datePart = parts[0] ? parts[0].slice(5) : '';
      const timePart = parts[1] || '';
      return parts.length === 2 ? `${datePart} ${timePart}` : item.hour;
    }
    if (item.week) return item.week;
    if (item.month) return item.month;
    return '';
  });

  const seriesData = rawList.map((item) => item.revenue);

  const series = [
    {
      name: 'Revenue',
      data: seriesData,
    },
  ];

  const options: any = {
    chart: {
      id: 'revenue-trend-chart',
      type: 'area',
      stacked: false,
      height: 320,
      fontFamily: 'inherit',
      background: 'transparent',
      zoom: {
        type: 'x',
        enabled: true,
        autoScaleYaxis: true,
      },
      toolbar: {
        show: true,
        autoSelected: 'zoom',
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    colors: ['#0091FF'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    title: {
      text: '',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    grid: {
      borderColor: 'hsl(var(--border) / 0.4)',
      strokeDashArray: 3,
    },
    yaxis: {
      labels: {
        formatter: function (val: number) {
          if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
          if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
          if (val >= 1000) return `₹${(val / 1000).toFixed(0)}k`;
          return `₹${val}`;
        },
        style: {
          colors: 'hsl(var(--muted-foreground))',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
      title: {
        text: 'Revenue (₹)',
        style: {
          color: 'hsl(var(--muted-foreground))',
          fontSize: '12px',
          fontWeight: 700,
        },
      },
    },
    xaxis: {
      type: 'category',
      categories: categories,
      labels: {
        style: {
          colors: 'hsl(var(--muted-foreground))',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    tooltip: {
      shared: false,
      theme: 'dark',
      y: {
        formatter: function (val: number) {
          return `₹${val.toLocaleString('en-IN')}`;
        },
      },
    },
  };

  return (
    <div className="w-full h-[350px] animate-fade-in">
      <Chart options={options} series={series} type="area" height={350} />
    </div>
  );
}

export function DashboardPage() {
  const user = useCurrentUser();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const { data, isLoading, isError, error } = useDashboard({
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });

  if (isLoading) {
    return (
      <div className="flex h-[450px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-semibold text-muted-foreground">
            Loading dashboard statistics...
          </p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-[450px] items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg font-bold text-destructive">Failed to load dashboard data</p>
          <p className="text-sm text-muted-foreground">
            {error?.response?.data?.message || error.message}
          </p>
        </div>
      </div>
    );
  }

  const overview = data?.overview || {
    totalUsers: 0,
    totalVendors: 0,
    totalCoupons: 0,
    totalRedeemedCoupons: 0,
    totalRevenue: 0,
  };

  const metrics = [
    {
      title: 'Total Users',
      value: overview.totalUsers.toLocaleString(),
      icon: Users,
      colorClass: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
    },
    {
      title: 'Total Merchants',
      value: overview.totalVendors.toLocaleString(),
      icon: Building,
      colorClass: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    },
    {
      title: 'Total Coupons',
      value: overview.totalCoupons.toLocaleString(),
      icon: Tag,
      colorClass: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    },
    {
      title: 'Redeemed Coupons',
      value: overview.totalRedeemedCoupons.toLocaleString(),
      icon: Activity,
      colorClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    },
    {
      title: 'Total Revenue',
      value: formatRevenue(overview.totalRevenue),
      icon: IndianRupee,
      colorClass: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
      tooltip: `₹${overview.totalRevenue.toLocaleString()}`,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header & Date Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b pb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard Overview</h2>
          <p className="text-muted-foreground text-sm">
            {user?.firstName && `Welcome back, ${user.firstName}! `}
            Here is a snapshot of your application metrics.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              From Date
            </label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 w-[150px] text-xs font-medium"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              To Date
            </label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 w-[150px] text-xs font-medium"
            />
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        {/* Revenue Graph Card */}
        <Card className="col-span-4 h-full flex flex-col border border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
            <CardDescription>Interactive revenue breakdown over time</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-1">
            <RevenueChart
              hourlyData={data?.hourlyOverview}
              weeklyData={data?.weeklyOverview}
              monthlyData={data?.monthlyOverview}
            />
          </CardContent>
        </Card>

        {/* Vendor Performance */}
        <Card className="col-span-3 h-full flex flex-col border border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle>Merchant Performance</CardTitle>
            <CardDescription>Top performing merchants by coupon sales</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="space-y-4 flex-1 max-h-[350px] overflow-y-auto pr-1 custom-scrollbar">
              {!(data?.topVendors || data?.vendorPerformance) || (data?.topVendors || data?.vendorPerformance)?.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No merchant performance stats found.
                </p>
              ) : (
                (data?.topVendors || data?.vendorPerformance)?.map((vendor) => (
                  <div
                    key={vendor.merchantId}
                    className="flex items-center justify-between border-b border-border/40 pb-3 last:border-0 last:pb-0"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-foreground">{vendor.merchantName}</p>
                      <p className="text-xs text-muted-foreground">
                        Sales:{' '}
                        <span className="font-semibold text-foreground">
                          {vendor.couponSalesCount} coupons
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-extrabold text-foreground">
                        ₹{vendor.revenueGenerated.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase">
                        Revenue
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
